// Import React modules
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
// Import other modules
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  updateProfile,
  signOut,
} from "@firebase/auth";
import { doc, setDoc, getDoc } from "firebase/firestore";
// Import Firebase config
import { auth, db, sendPasswordResetEmail } from "../firebase";
// Import Redux components
import { login, logout } from "../redux/features/userSlice";
// Import assets
import xMarkIcon from "../assets/icons/xmark.svg";
// Import CSS stylesheets
import "../styles/variables.css";
import "../styles/layoutStyles.css";
import "../styles/textStyles.css";
import "../styles/buttonStyles.css";

const OccupationList: { [key: string]: string } = {
  ADMINISTRATIVE: "Administrative",
  CHIROPRACTOR: "Chiropractor",
  CLINIC_MANAGER: "Clinic Manager",
  DOCTOR: "Doctor",
  EXERCISE_SPECIALIST: "Exercise Specialist",
  NURSE: "Nurse",
  OCCUPATIONAL_THERAPIST: "Occupational Therapist",
  OSTEOPATH: "Osteopath",
  PHYSIOTHERAPIST: "Physiotherapist",
  PODIATRIST: "Podiatrist",
  PSYCHOLOGIST: "Psychologist",
  STUDENT: "Student",
  SURGEON: "Surgeon",
  OTHER: "Other",
};

const CountryList: { [key: string]: string } = {
  UNITED_KINGDOM: "United Kingdom",
  //   GERMANY: "Germany",
};

function Welcome() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isInitialized, setIsInitialized] = useState(false); // State to check if Firebase Auth is initialized
  const [isSignUp, setIsSignUp] = useState(false);
  const [isSignIn, setIsSignIn] = useState(true);
  const [isWaitingSubscription, setIsWaitingSubscription] = useState(false);
  const [passwordCheck, setPasswordCheck] = useState("true");
  const [resetSent, setResetSent] = useState(false); // State to check if a password reset email has been sent
  const [showResetModal, setShowResetModal] = useState(false); // State to show the password reset modal
  const [resetError, setResetError] = useState(""); // State to display password reset errors
  const [resetEmail, setResetEmail] = useState(""); // State to store the email for password reset
  const [
    showResendVerificationEmailModal,
    setShowResendVerificationEmailModal,
  ] = useState(false);
  const [resendVerificationEmailSent, setResendVerificationEmailSent] =
    useState(false);
  const [resendVerificationEmailError, setResendVerificationEmailError] =
    useState("");
  const [uid, setUid] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [authError, setAuthError] = useState("");
  const [currentUserEmail, setCurrentUserEmail] = useState("");
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [occupation, setOccupation] = useState("ADMINISTRATIVE");
  const [country, setCountry] = useState("UNITED_KINGDOM");

  const inputStyle: React.CSSProperties = {
    boxSizing: "border-box",
    margin: "0 0 1rem 0",
    padding: "0.5rem",
    border: "1px solid #ccc",
    borderRadius: "4px",
    width: "100%",
  };

  useEffect(() => {
    window.Intercom("boot", {
      api_base: "https://api-iam.intercom.io",
      app_id: "zdag9apd",
    });

    const handleResize = () => {
      const newWindowWidth = window.innerWidth;
      setWindowWidth(newWindowWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((userAuth: any) => {
      if (userAuth) {
        setCurrentUserEmail(userAuth.email || "");
        setUid(userAuth.uid);
        if (userAuth.emailVerified) {
          setAuthError("");
          console.log("Dispatching!");
          console.log("Email: " + userAuth.email);
          console.log("UID: " + userAuth.uid);
          dispatch(
            login({
              email: userAuth.email,
              uid: userAuth.uid,
              displayName: userAuth.displayName,
              photoURL: userAuth.photoURL,
            })
          );
        } else {
          dispatch(logout());
        }
      } else {
        setCurrentUserEmail(""); // Clear the email if no user is signed in
      }
      setIsInitialized(true);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  function getErrorMessage(error: unknown) {
    if (error instanceof Error) return error.message;
    return String(error);
  }

  const checkPasswordsMatch = () => {
    const password = (
      document.getElementsByName("password")[0] as HTMLInputElement
    ).value;
    const confirmPassword = (
      document.getElementsByName("confirmPassword")[0] as HTMLInputElement
    ).value;
    setConfirmPassword(confirmPassword);
    if (password === confirmPassword) {
      setPasswordCheck("true");
    } else {
      setPasswordCheck("false");
    }
  };

  const handleSignUp = async (event: React.FormEvent) => {
    event.preventDefault(); // Prevent form from refreshing the page
    console.log("Signing up...");

    // Resetting the error message
    setAuthError("");

    // Form validation checks
    let errors = [];
    if (!firstName) {
      errors.push("First name is required.");
    }
    if (!lastName) {
      errors.push("Last name is required.");
    }
    if (!email) {
      errors.push("Email is required.");
    } else if (!email.includes("@")) {
      errors.push("Please enter a valid email address.");
    }
    if (!password) {
      errors.push("Password is required.");
    }
    if (!confirmPassword) {
      errors.push("Confirm password is required.");
    }

    // Display all errors
    if (errors.length > 0) {
      setAuthError(errors.join("\n"));
      return;
    }

    try {
      // Sign up logic
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      // Additional sign up steps
      await sendEmailVerification(userCredential.user);
      setUid(userCredential.user.uid);
      const userIdQueryParam = `client_reference_id=${encodeURIComponent(
        userCredential.user.uid
      )}`;
      console.log("client_reference_id at Welcome: ", uid);
      console.log("Verification email sent!");

      await updateProfile(userCredential.user, {
        displayName: firstName,
      });

      // Firestore user data setup
      const userRef = doc(db, "users", userCredential.user.uid);
      await setDoc(userRef, {
        email: email,
        firstName: firstName,
        lastName: lastName,
        account: "None",
        signUpDate: new Date(),
        occupation: occupation,
        country: country,
      });

      // Post sign-up actions
      setAuthError(
        "Sign up successful and verification email sent! Please verify your email before signing in."
      );
      console.log("New sign up successful and verification email sent.");

      await auth.signOut();

      // Redirect to Stripe payment page
      const stripePaymentLink = "https://buy.stripe.com/eVa6pV7glcpC1awbIK";
      const emailQueryParam = `prefilled_email=${encodeURIComponent(email)}`;
      const paymentLinkWithEmail = `${stripePaymentLink}?${emailQueryParam}&${userIdQueryParam}`;
      window.open(paymentLinkWithEmail, "_blank");

      setIsWaitingSubscription(true);
      setIsSignUp(false);
    } catch (error) {
      // Error handling
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred";
      setAuthError(errorMessage);
      console.log(errorMessage);
    }
  };

  // Add this useEffect outside of your handleSignUp function but inside your component
  useEffect(() => {
    if (authError) {
      alert(authError);
      console.log(authError);
      setAuthError("");
    }
  }, [authError]);

  const handleSignIn = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;
      if (user && !user.emailVerified) {
        setShowResendVerificationEmailModal(true);
      } else if (user && user.emailVerified) {
        const userDocRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(userDocRef);

        if (docSnap.exists()) {
          const userData = docSnap.data();

          if (
            userData &&
            (userData.account === "Free Trial" ||
              userData.account === "Paid") &&
            userData.stripeSessionId
          ) {
            console.log("Signed in! Attempting navigation...");
            dispatch(
              login({
                email: user.email || "",
                uid: user.uid || "",
                photoURL: user.photoURL || "",
                displayName: user.displayName || "",
              })
            );
            navigate("/home");
          } else {
            alert(
              "Please subscribe to a free trial to access Motics Copilot. A new window will be opened for you to complete this process."
            );
            dispatch(logout());
            await signOut(auth);
            // Use a timeout to ensure the error is shown before redirecting
            setTimeout(() => {
              redirectToStripe(user.email || "", user.uid || "");
            }, 500);
          }
        } else {
          setAuthError("User data does not exist. Please sign up.");
        }
      }
    } catch (error: any) {
      if (
        error.code === "auth/wrong-password" ||
        error.code === "auth/invalid-credential"
      ) {
        setAuthError("The password is invalid.");
      } else if (error.code === "auth/invalid-login-credentials") {
        setAuthError("Invalid login credentials.");
      } else if (error.code === "auth/missing-password") {
        setAuthError("Please enter your password.");
      } else if (error.code === "auth/invalid-email") {
        setAuthError("Please enter a valid email address.");
      } else if (error.code === "auth/user-not-found") {
        setAuthError("No user found with this email.");
      } else if (error.code === "auth/too-many-requests") {
        setAuthError(
          "Access to this account has been temporarily disabled due to many failed login attempts. You can immediately restore it by resetting your password or you can try again later."
        );
      } else {
        setAuthError(error.message);
      }
    }
  };

  function redirectToStripe(email: string, uid: string) {
    const stripePaymentLink = "https://buy.stripe.com/eVa6pV7glcpC1awbIK";
    const emailQueryParam = `prefilled_email=${encodeURIComponent(email)}`;
    const userIdQueryParam = `client_reference_id=${encodeURIComponent(uid)}`;
    const paymentLinkWithEmail = `${stripePaymentLink}?${emailQueryParam}&${userIdQueryParam}`;
    window.open(paymentLinkWithEmail, "_blank");
  }

  const handleResendVerificationEmail = async () => {
    setShowResendVerificationEmailModal(false);
    const user = auth.currentUser;
    if (user && !user.emailVerified) {
      try {
        await sendEmailVerification(user);
        alert("Verification email sent again. Please check your email.");
        setResendVerificationEmailSent(true);
        setResendVerificationEmailError("");
        await signOut(auth);
      } catch (error) {
        console.error("Error sending verification email:", error);
        setResendVerificationEmailSent(false);
        setResendVerificationEmailError(getErrorMessage(error));
        alert("Error sending verification email. Please try again later.");
      }
    }
  };

  const handleResetPassword = async () => {
    setIsResettingPassword(true);
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      console.log("Password reset email sent.");
      setResetSent(true);
      setResetError("");
      alert("Password reset email sent. Please check your email.");
      handleResetModalClose();
    } catch (error) {
      setResetSent(false);
      if (error instanceof Error && "code" in error) {
        if (error.code === "auth/invalid-email") {
          setResetError("Please enter a valid email address.");
        }
      }
      // setResetError(getErrorMessage(error));
      console.log(error);
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleResendVerificationEmailModalClose = () => {
    setShowResendVerificationEmailModal(false);
    setResendVerificationEmailSent(false);
    setResendVerificationEmailError("");
  };

  const handleResetModalClose = () => {
    setShowResetModal(false);
    setResetEmail("");
    setResetSent(false);
    setResetError("");
  };

  const handleResetModalSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    handleResetPassword();
  };

  const handleOpenPaymentWindow = (event: React.FormEvent) => {
    event.preventDefault();
    const stripePaymentLink = "https://buy.stripe.com/eVa6pV7glcpC1awbIK";
    const emailQueryParam = `prefilled_email=${encodeURIComponent(email)}`;
    const userIdQueryParam = `client_reference_id=${encodeURIComponent(uid)}`;
    const paymentLinkWithEmail = `${stripePaymentLink}?${emailQueryParam}&${userIdQueryParam}`;
    window.open(paymentLinkWithEmail, "_blank");
    console.log("client_reference_id at Welcome: ", uid);
  };

  function renderSignUpForm() {
    return (
      <form className="box-sizing-default welcome-form">
        <h2>Sign up to Motics Copilot</h2>
        <div
          style={{
            boxSizing: "border-box",
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <div style={{ width: "48%" }}>
            <label className="box-sizing-default">First Name</label>
            <input
              className="box-sizing-default"
              type="text"
              name="firstName"
              placeholder="Enter your first name"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              required
            />
          </div>
          <div style={{ width: "48%" }}>
            <label className="box-sizing-default">Last Name</label>
            <input
              className="box-sizing-default"
              type="text"
              name="lastName"
              placeholder="Enter your last name"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              required
            />
          </div>
        </div>
        <label className="box-sizing-default">Email</label>
        <input
          className="box-sizing-default"
          type="email"
          name="email"
          placeholder="Enter your email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
        <label className="box-sizing-default">Password</label>
        <input
          className="box-sizing-default"
          type="password"
          name="password"
          placeholder="Enter your password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
        />
        {password.length < 6 && password.length > 0 && (
          <div
            className="box-sizing-default alert-text"
            style={{ marginTop: "-18px" }}
          >
            Password must be at least 6 characters long
          </div>
        )}
        <label className="box-sizing-default">Confirm Password</label>
        <input
          className="box-sizing-default"
          type="password"
          name="confirmPassword"
          placeholder="Confirm your password"
          required
          onChange={checkPasswordsMatch}
        />
        {passwordCheck === "false" && (
          <div className="box-sizing-default alert-text">
            Passwords do not match
          </div>
        )}
        <label className="box-sizing-default">Occupation</label>
        <select
          style={inputStyle}
          value={occupation}
          onChange={(e) => setOccupation(e.target.value)}
        >
          {Object.entries(OccupationList).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <label className="box-sizing-default">Country</label>
        <div style={{ fontSize: "0.8rem", width: "100%" }}>
          Selecting a country specifies the user country and also sets the data
          storage location to comply with GDPR.
        </div>
        <select
          style={inputStyle}
          value={country}
          onChange={(e) => setCountry(e.target.value)}
        >
          {Object.entries(CountryList).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <button
          type="submit"
          className="button-primary"
          style={{ marginTop: "20px", marginBottom: "20px" }}
          disabled={!passwordCheck || password.length < 6}
          onClick={handleSignUp}
        >
          Sign Up
        </button>
        <p>
          Already have an account?{" "}
          <button
            className="button-link"
            onClick={() => {
              setIsSignUp(false);
              setIsSignIn(true);
            }}
          >
            Sign in
          </button>
        </p>
      </form>
    );
  }

  function renderResendVerificationEmailModal() {
    return (
      <div className="box-sizing-default modal-background">
        <div className="box-sizing-default modals" id="reset-password-modal">
          <button
            className="small-icon-button"
            onClick={handleResendVerificationEmailModalClose}
          >
            <img src={xMarkIcon} className="small-icon-button" alt="close" />
          </button>
          <h3>Email verification</h3>
          <p>Please verify your email before signing in.</p>
          <button
            className="button-primary"
            style={{ marginTop: "20px" }}
            onClick={handleResendVerificationEmail}
          >
            Resend verification email
          </button>
          {/* {resendVerificationEmailSent && (
            <p>Verification email sent. Please check your inbox and spam box.</p>
          )}
          {resendVerificationEmailError && <p>{resendVerificationEmailError}</p>} */}
        </div>
      </div>
    );
  }

  function renderResetModal() {
    return (
      <div className="box-sizing-default modal-background">
        <div className="box-sizing-default modals" id="reset-password-modal">
          <button className="small-icon-button" onClick={handleResetModalClose}>
            <img src={xMarkIcon} className="small-icon-button" alt="close" />
          </button>
          <h3>Reset password</h3>
          <input
            className="box-sizing-default"
            type="email"
            name="email"
            placeholder="Enter your email"
            value={resetEmail}
            onChange={(event) => {
              setResetEmail(event.target.value);
            }}
          />
          <button
            type="submit"
            className="button-primary"
            style={{ marginTop: "20px" }}
            onClick={handleResetModalSubmit}
            disabled={isResettingPassword}
          >
            Send reset email
          </button>
          {/* {resetSent && (
            <p>Password reset email sent. Please check your email.</p>
          )} */}
          {resetError && <p>{resetError}</p>}
        </div>
      </div>
    );
  }

  function renderSignInForm() {
    const handleFormSubmit = (event: React.FormEvent) => {
      event.preventDefault(); // Prevent form submission and page refresh

      // Clear the form inputs
      setEmail("");
      setPassword("");
    };
    return (
      <form
        className="box-sizing-default welcome-form"
        onSubmit={handleFormSubmit}
      >
        <h2>Welcome back</h2>
        <label className="box-sizing-default">Email</label>
        <input
          className="box-sizing-default"
          type="email"
          name="email"
          placeholder="Enter your email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
        <label className="box-sizing-default">Password</label>
        <input
          className="box-sizing-default"
          type="password"
          name="password"
          placeholder="Enter your password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
        />
        <p>{authError}</p>
        <button
          type="submit"
          className="button-primary"
          style={{ marginTop: "20px" }}
          onClick={handleSignIn}
        >
          Sign In
        </button>
        <div style={{ width: "100%", textAlign: "right" }}>
          <button
            className="button-link"
            onClick={(event) => setShowResetModal(true)}
          >
            {" "}
            Forgot password?
          </button>
          {showResetModal && renderResetModal()}
        </div>
        <p>
          Don't have an account?{" "}
          <button
            className="button-link"
            onClick={() => {
              setIsSignUp(true);
              setIsSignIn(false);
            }}
          >
            Sign up
          </button>
        </p>
      </form>
    );
  }

  function renderWaitingSubscription() {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <h2>
          Thank you for signing up!
          <br />
        </h2>
        <h3>
          To access Motics Copilot, please verify your email and sign up for a
          free trial. <br />
          <br />
          We have opened a new window for you to complete the free trial sign up
          process. If you do not see the window, please ensure pop-up windows
          are allowed and click{" "}
          <a href="#" onClick={handleOpenPaymentWindow}>
            here
          </a>
          .
        </h3>
      </div>
    );
  }

  if (!isInitialized) {
    return null;
  }
  return (
    <div className="box-sizing-default flexbox welcome-container">
      <div className="box-sizing-default welcome-items welcome-left-item">
        <div className="box-sizing-default images" id="welcome-image"></div>
      </div>
      <div className="box-sizing-default welcome-items welcome-right-item">
        <div
          className="box-sizing-default images"
          id="welcome-logo-image"
        ></div>
        {isSignUp && renderSignUpForm()}
        {isSignIn && renderSignInForm()}
        {isWaitingSubscription && renderWaitingSubscription()}
        {showResendVerificationEmailModal &&
          renderResendVerificationEmailModal()}
      </div>
    </div>
  );
}
export default Welcome;
